import { useEffect, useState } from 'react';
import { apiRequest } from './api.js';
import { cx } from './ui.js';

/**
 * Iniciais como último recurso.
 *
 * Nem todo mundo envia foto, e um vazio cinza na tela de quem está
 * esperando um carro chegar não ajuda ninguém a reconhecer o outro.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]![0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Busca a imagem com o token no cabeçalho e devolve uma URL de blob.
 *
 * A rota da foto exige Authorization, que `<img src>` não manda. E o
 * blob é revogado ao desmontar: sem isso cada corrida deixaria um objeto
 * preso na memória da aba, que num turno inteiro de motorista soma.
 */
function useAuthedImage(path: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        const res = await apiRequest(path);
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        // Sem foto a tela cai nas iniciais. Não é erro que mereça aviso.
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(null);
    };
  }, [path]);

  return url;
}

export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string;
  /** rota autenticada da foto; sem ela, mostra as iniciais */
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const url = useAuthedImage(src ?? null);
  const box = size === 'lg' ? 'h-20 w-20 text-2xl' : size === 'sm' ? 'h-10 w-10 text-sm' : 'h-14 w-14 text-lg';

  return (
    <div
      className={cx(
        'grid shrink-0 place-items-center overflow-hidden rounded-full bg-surface-2 font-bold text-muted',
        'ring-2 ring-line',
        box,
        className,
      )}
    >
      {url ? (
        <img src={url} alt={`Foto de ${name}`} className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </div>
  );
}
